import { useEffect, useState } from "react";
import {
  createFileRoute,
  Navigate,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";

import { BackButton, Header, Page } from "@/components/page.tsx";
import { cn } from "@/lib/utils.ts";
import {
  Modal,
  ModalBackdrop,
  ModalContent,
  ModalTitle,
  ModalWrapper,
} from "@/components/modal.tsx";
import { getPaymentFromSessionStorage } from "@/features/payment/utils.ts";
import { Button } from "@/components/ui/button";
import { useFeatureFlags } from "@/lib/runtime-config";
import { useAudioSlot } from "@/features/audio/kiosk-audio";
import {
  CashPaymentBody,
  CashPaymentFooter,
  CashPaymentOutcomeModals,
  useCashPayment,
} from "@/features/payment-cash/cash-payment";

export const Route = createFileRoute("/v5-cash-payment-screen")({
  component: RouteComponent,
  loader: () => {
    const payment = getPaymentFromSessionStorage();
    if (!payment) {
      throw redirect({
        to: "/start-screen",
        reloadDocument: true,
      });
    }

    return payment;
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const payment = Route.useLoaderData();
  const navigate = useNavigate();
  const cash = useCashPayment(payment);
  const { washStartGuide } = useFeatureFlags();
  const toSelector = () =>
    navigate({
      to: "/payment-selector-screen/$machineId/{-$dryerBundleId}",
      params: {
        machineId: payment.machineId,
        dryerBundleId: payment.dryerBundleId,
      },
    });

  return (
    <>
      <CashPaymentOutcomeModals
        cash={cash}
        payment={payment}
        onPaid={() => navigate({ to: "/payment-complete" })}
        onBackToSelector={toSelector}
      />
      {cash.isPaid &&
        // the wash start guide says "Sikeres fizetés!" itself
        (washStartGuide ? (
          <Navigate to="/payment-complete" />
        ) : (
          <ModalBackdrop>
            <ModalWrapper>
              <SuccessModal navigate={navigate} />
            </ModalWrapper>
          </ModalBackdrop>
        ))}
      <Page
        className={cn(
          "bg-background",
          cash.showBalanceCreditStep && "relative overflow-hidden bg-white",
        )}
        skipInactvityModal
      >
        <Header
          backArrowHref={
            cash.canAbort ? (
              <BackButton
                label={t("Megszakítás")}
                onClick={() => cash.abort.mutateAsync()}
              />
            ) : null
          }
        />
        <CashPaymentBody cash={cash} payment={payment} />
        <CashPaymentFooter cash={cash} />
      </Page>
    </>
  );
}

function SuccessModal({
  navigate,
}: {
  navigate: ReturnType<typeof useNavigate>;
}) {
  useAudioSlot("modal.cash-payment.success");
  const autoContinueSeconds = 10;
  const [secondsLeft, setSecondsLeft] = useState(autoContinueSeconds);

  const navigateToNext = () => {
    navigate({
      to: "/payment-complete",
    });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          navigateToNext();
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Modal>
      <ModalTitle>
        <Trans>Sikeres fizetés!</Trans>
      </ModalTitle>
      <ModalContent>
        <Button onClick={navigateToNext}>
          <Trans>Ok</Trans> ({secondsLeft})
        </Button>
      </ModalContent>
    </Modal>
  );
}
